"use client";

import { cn } from "@shared/src/utils/cn";
import { Button } from "@shared/src/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@shared/src/components/ui/avatar";
import { formatDistanceToNow } from "date-fns";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@shared/src/components/ui/tabs";
import { Chip } from "@shared/src/components/ui/chip";
import { X } from "lucide-react";
import { SheetClose } from "@shared/src/components/ui/sheet";
import { NotificationItem } from "@shared/src/stores/useNotificationStore";
import { useRouter } from "next/navigation";
import { ClipboardIcon } from "@shared/src/components/assets/icons";
import { ORDER_PAGE } from "@shared/src/config/page-links";
import { markNotificationsAsRead } from "@shared/src/components/providers/NotificationProvider";

interface NotificationsModalProps {
  className?: string;
  notifications?: NotificationItem[];
  context?: "dropdown" | "sheet";
  onNavigate?: () => void;
}

const TABS = [
  { id: "all", label: "All" },
  { id: "sell", label: "Sell" },
  { id: "buy", label: "Buy" },
] as const;

export function NotificationsModal({ className, notifications = [], context = "dropdown" }: NotificationsModalProps) {
  console.log("IN MODAL: ", notifications);
  const handleMarkAllAsRead = async () => {
    await markNotificationsAsRead(notifications.map(nt => nt.id as string));
  };

  const NotificationActions = ({
    type,
    link,
    messageId,
  }: {
    type: NotificationItem["type"];
    link?: string;
    messageId?: string;
  }) => {
    const router = useRouter();
    switch (type) {
      case "order":
        return (
          <Button
            variant="surface-tertiary"
            radius="full"
            className="self-center !h-10 px-8 py-4 mt-4"
            onClick={async () => {
              await markNotificationsAsRead([messageId as string]);
              router.push(link || "");
            }}
          >
            View Order
          </Button>
        );
      case "system":
        return (
          <Button variant="surface-tertiary" radius="full" className="self-center !h-10 px-8 py-4 mt-4">
            Take Action Now
          </Button>
        );
      case "cancelled":
        return (
          <Button variant="tertiary" radius="full" className="self-center !h-10 px-8 py-4 mt-4">
            Refund
          </Button>
        );
      default:
        return null;
    }
  };

  const formatNotificationMessage = (notification: NotificationItem) => {
    if (notification.type === "order") {
      return notification.message
        .replace("[BuyerUsername]", notification.buyerUsername || "")
        .replace("[Amount]", `${notification.amount?.value || ""} ${notification.amount?.currency || ""}`)
        .replace(
          "[FiatCurrencyAmount]",
          `${notification.fiatAmount?.currency || ""} ${notification.fiatAmount?.value || ""}`,
        );
    }
    return notification.message;
  };

  const filteredNotifications = (type?: "buy" | "sell") => {
    if (!type) return notifications;
    return notifications.filter(n => n.orderType === type);
  };

  const getUnreadCount = (type?: "buy" | "sell") => {
    const filtered = type ? notifications.filter(n => n.orderType === type) : notifications;
    return filtered.filter(n => !n.isRead).length;
  };

  const NotificationsList = ({ items }: { items: NotificationItem[] }) => {
    if (items.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center h-full py-16">
          <ClipboardIcon size={48} className="text-text-disabled mb-4" />
          <p className="text-text-secondary text-center">No notifications</p>
          <p className="text-text-tertiary text-small text-center mt-2">
            You will see notifications here when you have new activity
          </p>
        </div>
      );
    }

    return (
      <div className="space-y-6 px-6 py-2">
        {items.map((notification, i) => (
          <div key={i} className="relative pl-10 md:pl-2">
            {/* Read/Unread Status Dot */}
            <div
              className={cn(
                "absolute w-2 h-2 rounded-full",
                "left-0 md:-left-[22px] top-2",
                notification.isRead ? "bg-neutral-600" : "bg-primary-600",
              )}
            />

            <div className="flex gap-3">
              {notification.userAvatar && (
                <Avatar className="h-10 w-10 shrink-0">
                  <AvatarImage src={notification.userAvatar} />
                  <AvatarFallback>U</AvatarFallback>
                </Avatar>
              )}
              <div className="flex-1 min-w-0">
                <div className="space-y-1">
                  <p className="text-text-primary font-bold break-words">{notification.title}</p>
                  <p className="text-small text-text-secondary break-words">
                    {/* {formatNotificationMessage(notification)} */}
                    {notification.message}
                  </p>
                  <p className="text-micro text-text-tertiary">
                    {formatDistanceToNow(notification.timestamp, {
                      addSuffix: true,
                    })}
                  </p>
                </div>
                <NotificationActions
                  type={notification.type}
                  link={notification.orderId && ORDER_PAGE(notification.orderId)}
                  messageId={notification.id}
                />
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div
      className={cn(
        "flex flex-col bg-surfaces-bg-secondary [&>button]:hidden overflow-hidden",
        context === "sheet" ? "w-full md:w-[400px] h-full md:h-full" : "w-[400px] max-h-[80vh] overflow-hidden",
        className,
      )}
    >
      {context === "sheet" && (
        <SheetClose asChild>
          <div className="text-text-tertiary group w-full hover:text-text-primary cursor-pointer flex justify-end px-4 pt-4 md:hidden">
            <X className="w-10 h-10 rounded-full p-2 group-hover:border border-surfaces-bg-tertiary" />
          </div>
        </SheetClose>
      )}
      <div className="flex items-center justify-between p-4 flex-shrink-0">
        <h6>Notifications</h6>
        <button
          onClick={handleMarkAllAsRead}
          className="text-small text-text-secondary hover:text-text-primary cursor-pointer"
        >
          Mark all as read
        </button>
      </div>

      <Tabs
        defaultValue="all"
        className={`w-full flex flex-col overflow-hidden ${
          notifications.length > 0 ? "h-full md:h-[600px]" : "h-full"
        }`}
      >
        <TabsList variant="underline" className="px-4 text-small justify-start flex-shrink-0">
          {TABS.map(tab => (
            <TabsTrigger
              key={tab.id}
              value={tab.id}
              variant="underline"
              className={cn(
                "pb-2 relative data-[state=active]:after:absolute data-[state=active]:after:bottom-0 data-[state=active]:after:left-0 data-[state=active]:after:right-0 data-[state=active]:after:h-0.5 data-[state=active]:after:bg-text-primary",
                tab.id !== "all" && "ml-4",
              )}
            >
              {tab.label}
              {getUnreadCount(tab.id === "all" ? undefined : (tab.id as "buy" | "sell")) > 0 && (
                <Chip className="ml-2" size="sm">
                  {getUnreadCount(tab.id === "all" ? undefined : (tab.id as "buy" | "sell"))}
                </Chip>
              )}
            </TabsTrigger>
          ))}
        </TabsList>

        <div className={cn("overflow-y-auto custom-scrollbar", context === "sheet" ? "flex-1" : "")}>
          {TABS.map(tab => (
            <TabsContent key={tab.id} value={tab.id} className={context === "sheet" ? "h-full" : ""}>
              <NotificationsList
                items={filteredNotifications(tab.id === "all" ? undefined : (tab.id as "buy" | "sell"))}
              />
            </TabsContent>
          ))}
        </div>
      </Tabs>
    </div>
  );
}
